import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Loader2, X, Star, CheckCircle, Clock, Eye, EyeOff } from "lucide-react";
import { getTemoignages, createTemoignage, updateTemoignage, deleteTemoignage } from "@/lib/api";

export const TemoignagesCMS = () => {
  const [temoignages, setTemoignages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeFilter, setActiveFilter] = useState("attente"); // attente | publies | tous
  const [formData, setFormData] = useState({
    nom: "", email: "", texte: "", photo: "", type_temoignage: "voyageur",
    logement_nom: "", note: 5, actif: true,
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await getTemoignages();
      setTemoignages(res.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, en_attente: false };
      if (editing) {
        await updateTemoignage(editing.id, payload);
        toast.success("Avis mis à jour !");
      } else {
        await createTemoignage(payload);
        toast.success("Avis créé !");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (tem) => {
    setEditing(tem);
    setFormData({
      nom: tem.nom, email: tem.email || "", texte: tem.texte,
      photo: tem.photo || "", type_temoignage: tem.type_temoignage,
      logement_nom: tem.logement_nom || "", note: tem.note, actif: tem.actif,
    });
    setShowModal(true);
  };

  const handleValider = async (tem) => {
    try {
      await updateTemoignage(tem.id, { ...tem, actif: true, en_attente: false });
      toast.success("Avis publié ✓");
      loadData();
    } catch { toast.error("Erreur"); }
  };

  const handleRefuser = async (tem) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    try {
      await deleteTemoignage(tem.id);
      toast.success("Avis supprimé");
      loadData();
    } catch { toast.error("Erreur"); }
  };

  const handleToggleActif = async (tem) => {
    try {
      await updateTemoignage(tem.id, { ...tem, actif: !tem.actif });
      toast.success(tem.actif ? "Avis masqué" : "Avis publié");
      loadData();
    } catch { toast.error("Erreur"); }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ nom: "", email: "", texte: "", photo: "", type_temoignage: "voyageur", logement_nom: "", note: 5, actif: true });
  };

  const enAttente = temoignages.filter(t => t.en_attente);
  const publies   = temoignages.filter(t => !t.en_attente && t.actif);
  const masques   = temoignages.filter(t => !t.en_attente && !t.actif);

  const filtered = activeFilter === "attente" ? enAttente
    : activeFilter === "publies" ? [...publies, ...masques]
    : temoignages;

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-[#004333]" /></div>;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "attente", label: "En attente", count: enAttente.length, color: "orange" },
            { key: "publies", label: "Publiés / Masqués", count: publies.length + masques.length, color: "green" },
            { key: "tous",    label: "Tous", count: temoignages.length, color: "gray" },
          ].map(f => (
            <button key={f.key} onClick={() => setActiveFilter(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeFilter === f.key ? "bg-[#004333] text-white" : "bg-white border border-gray-200 text-gray-500 hover:border-[#004333]/30"
              }`}>
              {f.label}
              {f.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                  activeFilter === f.key ? "bg-white/20 text-white" :
                  f.color === "orange" ? "bg-orange-100 text-orange-600" : "bg-gray-100 text-gray-500"
                }`}>{f.count}</span>
              )}
            </button>
          ))}
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nouvel avis
        </button>
      </div>

      {/* Avis en attente — bannière alerte */}
      {enAttente.length > 0 && activeFilter === "attente" && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-5 flex items-center gap-3">
          <Clock className="w-5 h-5 text-orange-500 flex-shrink-0" />
          <p className="text-sm text-orange-700 font-medium">
            {enAttente.length} avis en attente de validation — Validez ou modifiez avant publication.
          </p>
        </div>
      )}

      {/* Liste */}
      {filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((tem) => (
            <div key={tem.id} className={`bg-white border rounded-2xl p-5 ${
              tem.en_attente ? "border-orange-200 bg-orange-50/30" :
              !tem.actif ? "border-gray-200 opacity-60" : "border-gray-200"
            }`}>
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0 text-sm ${
                  tem.en_attente ? "bg-orange-400" : "bg-[#004333]"
                }`}>
                  {tem.nom.charAt(0).toUpperCase()}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-[#004333] text-sm">{tem.nom}</span>
                    {tem.email && <span className="text-xs text-gray-400">{tem.email}</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      tem.type_temoignage === "proprietaire" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                    }`}>
                      {tem.type_temoignage === "proprietaire" ? "Propriétaire" : "Voyageur"}
                    </span>
                    {tem.logement_nom && (
                      <span className="text-xs bg-[#004333]/10 text-[#004333] px-2 py-0.5 rounded-full">
                        {tem.logement_nom}
                      </span>
                    )}
                    {tem.en_attente && (
                      <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                        <Clock className="w-3 h-3" /> En attente
                      </span>
                    )}
                    {!tem.en_attente && !tem.actif && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Masqué</span>
                    )}
                  </div>

                  {/* Stars */}
                  <div className="flex gap-0.5 mb-2">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} className={`w-3.5 h-3.5 ${n <= tem.note ? "fill-[#C9922A] text-[#C9922A]" : "text-gray-200"}`} />
                    ))}
                  </div>

                  <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">"{tem.texte}"</p>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  {tem.en_attente ? (
                    <>
                      <button onClick={() => handleValider(tem)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition-colors">
                        <CheckCircle className="w-3.5 h-3.5" /> Valider
                      </button>
                      <button onClick={() => handleEdit(tem)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#004333] text-white text-xs font-medium rounded-lg hover:bg-[#005544] transition-colors">
                        <Edit className="w-3.5 h-3.5" /> Modifier
                      </button>
                      <button onClick={() => handleRefuser(tem)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-500 text-xs font-medium rounded-lg hover:bg-red-100 transition-colors border border-red-200">
                        <X className="w-3.5 h-3.5" /> Refuser
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleToggleActif(tem)}
                        className={`p-1.5 rounded-lg transition-colors ${tem.actif ? "text-green-500 hover:bg-green-50" : "text-gray-400 hover:bg-gray-100"}`}>
                        {tem.actif ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleEdit(tem)} className="p-1.5 text-gray-400 hover:text-[#004333] rounded-lg hover:bg-gray-100 transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleRefuser(tem)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center text-gray-400">
          {activeFilter === "attente" ? "Aucun avis en attente de validation." : "Aucun avis."}
        </div>
      )}

      {/* Modal créer / modifier */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-[#004333]">
              <h2 className="text-white font-semibold">{editing ? "Modifier l'avis" : "Nouvel avis"}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Nom <span className="text-red-500">*</span></label>
                  <input type="text" required value={formData.nom}
                    onChange={e => setFormData({...formData, nom: e.target.value})}
                    className="input-hh" autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <input type="email" value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className="input-hh" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
                  <select value={formData.type_temoignage}
                    onChange={e => setFormData({...formData, type_temoignage: e.target.value})}
                    className="input-hh">
                    <option value="voyageur">Voyageur</option>
                    <option value="proprietaire">Propriétaire</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Logement concerné</label>
                  <input type="text" value={formData.logement_nom}
                    onChange={e => setFormData({...formData, logement_nom: e.target.value})}
                    placeholder="Nom du logement" className="input-hh" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Avis <span className="text-red-500">*</span></label>
                <textarea rows={5} required value={formData.texte}
                  onChange={e => setFormData({...formData, texte: e.target.value})}
                  className="input-hh resize-none" placeholder="Témoignage du client..." />
                <p className="mt-1 text-xs text-gray-400">{formData.texte.length} caractères</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" onClick={() => setFormData({...formData, note: n})} className="p-1">
                      <Star className={`w-6 h-6 transition-colors ${n <= formData.note ? "fill-[#C9922A] text-[#C9922A]" : "text-gray-200 hover:text-gray-300"}`} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <button type="button" onClick={() => setFormData(f => ({...f, actif: !f.actif}))}
                    className={`relative w-10 h-5 rounded-full transition-colors ${formData.actif ? "bg-green-500" : "bg-gray-300"}`}>
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.actif ? "translate-x-5" : ""}`} />
                  </button>
                  <span className={`text-sm ${formData.actif ? "text-green-600 font-medium" : "text-gray-400"}`}>
                    {formData.actif ? "Publié sur le site" : "Masqué"}
                  </span>
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Annuler</button>
                <button type="submit" className="btn-primary flex-1">{editing ? "Mettre à jour" : "Créer"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemoignagesCMS;
